import { alpha, type Components, type Theme } from "@mui/material";
import type { SubscriptionPlanType } from "@/types/subscription-plans";

// TODO: remove "ghost" variant, migrate them to "text"

declare module "@mui/material/Button" {
	interface ButtonPropsSizeOverrides {
		icon: true;
		iconLarge: true;
		iconMedium: true;
		iconSmall: true;
	}

	interface ButtonPropsVariantOverrides {
		icon: true;
		ghost: true;
	}

	interface ButtonPropsColorOverrides {
		white: true;
		muted: true;
		destructive: true;
		audiences: true;
		starter: true;
		essentials: true;
		premium: true;
	}
}

export const MuiButton: Components<Theme>["MuiButton"] = {
	defaultProps: {
		disableFocusRipple: true,
		disableRipple: true,
		disableTouchRipple: true,
		variant: "contained",
	},
	styleOverrides: {
		root: ({ theme }) => ({
			"&:focus-visible": {
				outline: `2px solid ${theme.palette.primary.main}`,
				outlineOffset: "2px",
				boxShadow: "none",
			},
		}),
	},
	variants: [
		{
			props: {},
			style: ({ theme }) => ({
				borderRadius: theme.spacing(2),
				boxShadow: "none",
				fontWeight: 600,
				lineHeight: 1.25,
				paddingInline: theme.spacing(3),
				textTransform: "initial",
				"&>svg": { flexShrink: 0 },
				"&.MuiButton-loading": { opacity: 0.75 },
			}),
		},
		{
			props: { size: "small" },
			style: ({ theme }) => ({
				borderRadius: theme.spacing(1),
				fontSize: theme.typography.pxToRem(14),
				paddingInline: theme.spacing(1.5),
				"& svg": {
					height: theme.spacing(2.25),
					width: theme.spacing(2.25),
				},
			}),
		},
		{
			props: { size: "medium" },
			style: ({ theme }) => ({
				fontSize: theme.typography.pxToRem(16),
				svg: {
					height: theme.spacing(2.5),
					width: theme.spacing(2.5),
				},
				[theme.breakpoints.up("xs")]: {
					height: theme.spacing(7),
					".MuiCircularProgress-root": {
						height: "1.5rem !important",
						width: "1.5rem !important",
					},
				},
				[theme.breakpoints.up("md")]: {
					height: theme.spacing(5.5),
					".MuiCircularProgress-root": {
						height: "1.25rem !important",
						width: "1.25rem !important",
					},
				},
			}),
		},
		{
			props: { size: "large" },
			style: ({ theme }) => ({
				fontSize: theme.typography.pxToRem(18),
				minHeight: theme.spacing(7),
				paddingBlock: theme.spacing(1.75),
			}),
		},
		{
			props: { size: "icon" },
			style: ({ theme }) => ({
				borderRadius: theme.spacing(1),
				height: theme.spacing(4.5),
				minWidth: 0,
				padding: 0,
				width: theme.spacing(4.5),
				"&>svg": {
					height: theme.spacing(3),
					width: theme.spacing(3),
				},
			}),
		},
		{
			props: { size: "iconSmall" },
			style: ({ theme }) => ({
				borderRadius: theme.spacing(1),
				height: theme.spacing(4),
				minWidth: 0,
				padding: 0,
				width: theme.spacing(4),
				"&>svg": {
					height: theme.spacing(2),
					width: theme.spacing(2),
				},
			}),
		},
		{
			props: { size: "iconMedium" },
			style: ({ theme }) => ({
				height: theme.spacing(6),
				minWidth: 0,
				padding: 0,
				width: theme.spacing(6),
				"&>svg": {
					height: theme.spacing(3),
					width: theme.spacing(3),
				},
			}),
		},
		{
			props: { size: "iconLarge" },
			style: ({ theme }) => ({
				height: theme.spacing(7.5),
				minWidth: 0,
				padding: 0,
				width: theme.spacing(7.5),
				"&>svg": {
					height: theme.spacing(3),
					width: theme.spacing(3),
				},
			}),
		},
		{
			props: { variant: "contained", color: "primary" },
			style: ({ theme }) => ({
				backgroundColor: theme.palette.primary.main,
				backgroundImage: theme.palette.gradients.primary.main,
				boxShadow: `0 0 1px 1px ${theme.palette.text.primary} inset`,
				"&:hover": { backgroundImage: "none" },
				"&.Mui-disabled:not(.MuiButton-loading)": {
					color: alpha(theme.palette.primary.contrastText, 0.75),
					opacity: 0.5,
				},
				".MuiCircularProgress-root": {
					color: theme.palette.primary.contrastText,
				},
			}),
		},
		{
			props: { variant: "contained", color: "secondary" },
			style: ({ theme }) => ({
				backgroundColor: theme.palette.secondary.main,
				borderColor: theme.palette.border,
				borderStyle: "solid",
				borderWidth: 1,
				"&.MuiButton-loading": {
					backgroundColor: theme.palette.secondary.main,
				},
				".MuiCircularProgress-root": {
					color: theme.palette.secondary.contrastText,
				},
			}),
		},
		{
			props: { variant: "contained", color: "white" },
			style: {
				backgroundColor: "#F8F8F8",
				color: "#1B1B1B",
				"&:hover": {
					backgroundColor: "#EAEAEA",
				},
				".MuiCircularProgress-root": {
					color: "#1B1B1B",
				},
			},
		},
		{
			props: { variant: "contained", color: "muted" },
			style: ({ theme }) => ({
				backgroundColor: theme.palette.muted.main,
				color: theme.palette.text.primary,
				"&:hover": {
					backgroundColor: theme.palette.muted.dark,
					boxShadow: "none",
				},
			}),
		},
		{
			props: { variant: "contained", color: "destructive" },
			style: ({ theme }) => ({
				backgroundColor: theme.palette.error.background,
				color: theme.palette.text.primary,
				svg: {
					color: theme.palette.error.main,
				},
				"&:hover": {
					backgroundColor: theme.palette.error.main,
					boxShadow: "none",
					color: theme.palette.error.contrastText,
					svg: {
						color: theme.palette.error.contrastText,
					},
				},
			}),
		},
		{
			props: { variant: "contained", color: "audiences" },
			style: ({ theme }: { theme: Theme }) => ({
				backgroundColor: theme.palette.audiences.dark,
				backgroundImage: theme.palette.gradients.audiences.toBottom,
				boxShadow: `1px -1px 4px ${theme.palette.audiences.light} inset`,
				color: theme.palette.audiences.contrastText,
				"&:hover": {
					backgroundImage: "none",
				},
				".MuiCircularProgress-root": {
					color: "white",
				},
			}),
		},
		{
			props: { variant: "outlined" },
			style: ({ theme }) => ({
				color: theme.palette.text.primary,
				borderColor: theme.palette.border,
				"&:hover": {
					backgroundColor: theme.palette.muted.main,
				},
			}),
		},
		{
			props: { variant: "outlined", color: "muted" },
			style: ({ theme }) => ({
				backgroundColor: theme.palette.muted.main,
				"&:hover": {
					backgroundColor: theme.palette.muted.dark,
				},
			}),
		},
		{
			props: { variant: "outlined", color: "destructive" },
			style: ({ theme }) => ({
				backgroundColor: theme.palette.error.background,
				borderColor: theme.palette.error.border,
				color: theme.palette.text.primary,
				svg: {
					color: theme.palette.error.main,
				},
				"&:hover": {
					backgroundColor: theme.palette.error.main,
					borderColor: theme.palette.error.main,
					boxShadow: "none",
					color: theme.palette.error.contrastText,
					svg: {
						color: theme.palette.error.contrastText,
					},
				},
			}),
		},
		{
			props: { variant: "text", color: "muted" },
			style: ({ theme }) => ({
				color: theme.palette.text.disabled,
				"&:hover": {
					color: theme.palette.primary.main,
				},
			}),
		},
		{
			props: { variant: "ghost", color: "primary" },
			style: ({ theme }) => ({
				color: theme.palette.text.primary,
				"&:hover": {
					boxShadow: "none",
					color: theme.palette.primary.main,
					backgroundColor: theme.palette.muted.main,
				},
			}),
		},
		{
			props: { variant: "ghost", color: "muted" },
			style: ({ theme }) => ({
				color: theme.palette.text.disabled,
				"&:hover": {
					color: theme.palette.primary.main,
				},
			}),
		},
		createOutlinedStatusColor("info"),
		createOutlinedStatusColor("success"),
		createOutlinedStatusColor("warning"),
		createOutlinedStatusColor("error"),
		createSubscriptionColor("starter"),
		createSubscriptionColor("essentials"),
		createSubscriptionColor("premium"),
	],
};

function createOutlinedStatusColor(
	color: "info" | "success" | "warning" | "error",
) {
	return {
		props: { variant: "outlined" as const, color },
		style: ({ theme }: { theme: Theme }) => ({
			borderColor: theme.palette[color].border,
			"&:hover": {
				backgroundColor: "transparent",
				borderColor: theme.palette[color].main,
				color: theme.palette[color].main,
			},
		}),
	};
}

function createSubscriptionColor(color: SubscriptionPlanType) {
	return {
		props: { variant: "contained" as const, color },
		style: ({ theme }: { theme: Theme }) => ({
			backgroundColor: theme.palette[color].dark,
			backgroundImage: theme.palette.gradients.subscription[color].toBottom,
			color: theme.palette[color].contrastText,
			"&:hover": {
				backgroundImage: "none",
			},
			".MuiCircularProgress-root": {
				color: "white",
			},
		}),
	};
}
